import React, { memo, useState, useLayoutEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * SortableCard - Wrapper that makes a card sortable with dnd-kit
 * Provides smooth iOS-style reordering animation for cards within and across columns
 * 
 * When dragging, leaves a placeholder to preserve column height and prevent layout shifts.
 * The actual card is rendered in DragOverlay by the parent component.
 */
const SortableCard = memo(({ id, children, disabled = false, swimlaneId = null }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ 
    id,
    data: {
      type: 'card',
      cardId: id,
      swimlaneId, // Include swimlane ID for collision detection filtering
    },
    disabled,
  });

  // Track the card's height to preserve space when dragging
  const innerRef = useRef(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(null);

  // Capture height BEFORE drag starts using layout effect
  // We need to measure before the card becomes invisible
  useLayoutEffect(() => {
    if (isDragging && innerRef.current && placeholderHeight === null) {
      // Measure the actual card content height
      const cardEl = innerRef.current.querySelector('.kanban-card');
      if (cardEl) {
        setPlaceholderHeight(cardEl.offsetHeight);
      }
    } else if (!isDragging && placeholderHeight !== null) {
      setPlaceholderHeight(null);
    }
  }, [isDragging, placeholderHeight]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`sortable-card-wrapper ${isDragging ? 'sortable-card-wrapper--dragging' : ''} ${isOver ? 'sortable-card-wrapper--over' : ''}`}
      data-task-id={id}
      data-card-id={id}
      {...attributes}
    >
      {/* Always render the card but hide it when dragging */}
      <div 
        ref={innerRef}
        style={{ display: isDragging ? 'none' : 'block' }}
      >
        {React.cloneElement(children, { 
          dragListeners: listeners,
          isDraggingCard: isDragging,
        })}
      </div>
      
      {/* Show placeholder when dragging to preserve column height */}
      {isDragging && (
        <div 
          className="sortable-card-placeholder"
          style={{ height: placeholderHeight ? `${placeholderHeight}px` : '80px' }}
        />
      )}
    </div>
  );
});

export default SortableCard;

